import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';
import './styles/privacy.css';
import { header } from './components/header.js';
import { footer } from './components/footer.js';
import { initPromo } from './components/promo.js';
import { trackPageView, trackVisitRecency } from './lib/analytics.js';
import { initTheme } from './lib/theme.js';

initTheme();

document.getElementById('app').innerHTML = `
  ${header()}
  <main class="wrap privacy-page">
    <h1>Privacy &amp; how it works</h1>
    <p class="lead">cruxdiff compares your files <b>entirely inside this browser tab</b>. Your data is never uploaded, and no script on this page can read it. Here's exactly how — and how you can verify it.</p>

    <section>
      <h2>The claim, precisely</h2>
      <p>Our claim is: <b>your data never leaves your device, and no script on this page can read it.</b> That's a stronger, more honest promise than "zero network requests" — the page still loads fonts and (later) one ad unit, which are requests. What matters is that the <em>comparison</em> makes none, and nothing can read your inputs.</p>
    </section>

    <section>
      <h2>How it works</h2>
      <ul>
        <li><b>100% client-side.</b> Parsing and diffing run in your browser — the text engine, JSON/YAML structural diff, and CSV key-matching all execute in a background Web Worker, never on a server.</li>
        <li><b>No uploads, no accounts.</b> There's nothing to sign up for and no file picker that sends anything anywhere.</li>
        <li><b>Sharing without a server.</b> A share link compresses the whole diff into the URL <em>fragment</em> (after the <code>#</code>). Fragments are never sent in HTTP requests, so even a shared link keeps your data on your device.</li>
        <li><b>Strict Content-Security-Policy.</b> <code>connect-src 'self'</code> means no script on the page can open a connection to another origin to exfiltrate your input.</li>
        <li><b>Works offline.</b> After the first visit the app is cached, so it keeps working with your wifi off.</li>
      </ul>
    </section>

    <section>
      <h2>Verify it yourself</h2>
      <ul>
        <li><b>Turn off wifi</b> and run a comparison — it still works.</li>
        <li>Open <b>DevTools → Network</b>, run a diff, and watch: zero requests during the comparison.</li>
        <li>Read the <b>CSP</b> — in this page's <code>&lt;meta http-equiv="Content-Security-Policy"&gt;</code> and, on our host, the response headers — <code>connect-src 'self'</code>.</li>
      </ul>
    </section>

    <section>
      <h2>Analytics &amp; ads</h2>
      <p><b>The Pro interest form is the one place we store something you send us.</b> If you submit it, the email address, the two answers and the optional note go into our own database on Cloudflare — nothing else. No IP address, no browser fingerprint, no identifier, and nothing that can be joined to the anonymous page events described below. We keep it for 12 months, or until Pro ships if that comes first, and use it only to decide what to build and to reply to you. Email <a href="mailto:hello@cruxdiff.com?subject=Delete%20my%20Pro%20interest">hello@cruxdiff.com</a> to have it deleted sooner. Your comparison files are never part of it.</p>
      <p>One date is kept in this browser's local storage — the day of your last visit, so a visit can be counted as new or returning. It is a date and nothing else: no identifier, no counter, no history, and nothing that links one visit to another. Clearing site data removes it, and <em>Do Not Track</em> stops it being read or sent.</p>
      <p>Page analytics record <b>page events only</b> — which features are used and where people drop off — <b>never</b> your input buffers or diff content. They run <b>first-party</b> (no third-party tracker), with <b>no cookies</b> and <b>no device or user identifier</b>, and we honour <em>Do Not Track</em>.</p>
      <p>We record <b>coarse, aggregate location</b> — country, region, and city — derived from your connection at our edge. We never collect precise coordinates, and location is <b>never linked to you or to your data</b>. City-level figures are approximate.</p>
      <p>The only ad today is a <b>first-party promo card</b> for our own game site, TimeWellLost, shown above the footer. It's drawn inline by this page — <b>no ad network, no third-party script, no extra network request</b>. Seeing or clicking it records a page event that names <em>which of our creatives</em> was shown, nothing else. It only ever appears below the results, never inside them. If a third-party ad unit ever ships, it will load in an isolated iframe with no access to the app, and this page will say so.</p>
    </section>

    <p class="back"><a href="/">← Back to cruxdiff</a></p>
  </main>
  ${footer()}
`;

initPromo();

// Register the offline service worker here too, so a visitor whose first landing
// is the privacy page still gets the app cached (prod/preview only — never dev).
// Every page reports its own view. Without this only the home page was counted,
// which made this page invisible AND quietly inflated nothing — the funnel's
// denominator was home-only while the house ad on this very page was reporting
// impressions (REL-071).
trackPageView('privacy');
trackVisitRecency();

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  addEventListener('load', () => navigator.serviceWorker.register('/service-worker.js').catch(() => {}));
}
